import React, { useState, useEffect } from 'react';
import { X, Save, Calculator, CheckSquare, Square, Eye, AlertCircle, FileSpreadsheet, FileText } from 'lucide-react';
import { medicaoService } from '../../services/medicaoService';
import { MedicoesAgregado, StatusMedicao } from '../../types/medicao';
import { supabase } from '../../lib/supabase';
import { VisualizarOSModal } from '../../pages/VisualizarOSModal';
import { MedicaoOSList } from './MedicaoOSList';
import { exportMedicaoToExcel, exportMedicaoToPdf, resolveTipoVeiculoDisplay } from '../../services/medicaoExportService';
import { formatDateBR } from '../../utils/dateUtils';
import { fixEncodingIssues } from '../../utils/textUtils';

interface MedicaoAgregadoFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  selectedMedicao?: MedicoesAgregado | null;
}

export function MedicaoAgregadoFormModal({ isOpen, onClose, onSuccess, selectedMedicao }: MedicaoAgregadoFormModalProps) {
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [agregados, setAgregados] = useState<{ id: number, nome: string }[]>([]);
  const [statusList, setStatusList] = useState<StatusMedicao[]>([]);
  
  const [agregadoId, setAgregadoId] = useState<number | ''>('');
  const [periodoInicial, setPeriodoInicial] = useState('');
  const [periodoFinal, setPeriodoFinal] = useState('');
  const [valorMedicao, setValorMedicao] = useState<number>(0);
  const [statusMedicaoId, setStatusMedicaoId] = useState<number>(1); // 1 = Em Análise

  // Unlinked OS fetched for calculation/selection
  const [fetchedOS, setFetchedOS] = useState<any[]>([]);
  const [selectedOSIds, setSelectedOSIds] = useState<number[]>([]);
  const [hasCalculated, setHasCalculated] = useState(false);
  const [viewingOsId, setViewingOsId] = useState<number | null>(null);

  useEffect(() => {
    if (isOpen) {
      fetchData();
      if (selectedMedicao) {
        setAgregadoId(selectedMedicao.agregado_id || '');
        setPeriodoInicial(selectedMedicao.periodo_inicial || selectedMedicao.data_inicial || '');
        setPeriodoFinal(selectedMedicao.periodo_final || selectedMedicao.data_final || '');
        setValorMedicao(selectedMedicao.valor_medicao || selectedMedicao.valor_repassar || 0);
        setStatusMedicaoId(selectedMedicao.status_medicao_id || 1);
        setFetchedOS([]);
        setSelectedOSIds([]);
        setHasCalculated(false);
      } else {
        resetForm();
      }
    }
  }, [isOpen, selectedMedicao]);

  const fetchData = async () => {
    try {
      const [agregadosData, statuses] = await Promise.all([
        medicaoService.getAgregadosMedicao(),
        medicaoService.getStatusMedicao()
      ]);
      setAgregados(agregadosData || []);
      setStatusList(statuses || []);
    } catch (e) {
      console.error('Error fetching agregado data:', e);
    }
  };

  const resetForm = () => {
    setAgregadoId('');
    setPeriodoInicial('');
    setPeriodoFinal('');
    setValorMedicao(0);
    setStatusMedicaoId(1);
    setFetchedOS([]);
    setSelectedOSIds([]);
    setHasCalculated(false);
  };

  const handleGerarMedicao = async () => {
    if (!agregadoId || !periodoInicial || !periodoFinal) {
      alert('Preencha o Agregado, Período Inicial e Período Final para puxar as Ordens de Serviço.');
      return;
    }

    setCalculating(true);
    try {
      const result = await medicaoService.getUnlinkedOSForMedicaoAgregado(
        Number(agregadoId), 
        periodoInicial, 
        periodoFinal,
        selectedMedicao?.id
      );
      
      setFetchedOS(result.ordens || []);
      const allIds = (result.ordens || []).map(o => o.id);
      setSelectedOSIds(allIds);
      setValorMedicao(result.total || 0);
      setHasCalculated(true);

      if (result.count === 0) {
        alert('Nenhuma Ordem de Serviço com status "Finalizada" ou "Aguardando Assinatura" encontrada para este agregado no período selecionado.');
      }
    } catch (error: any) {
      console.error('Erro ao buscar OSs para medição de agregado:', error);
      alert('Erro ao calcular medição: ' + (error?.message || 'Verifique os dados informados.'));
    } finally {
      setCalculating(false);
    }
  };

  const toggleSelectOS = (id: number) => {
    let newSelected: number[];
    if (selectedOSIds.includes(id)) {
      newSelected = selectedOSIds.filter(i => i !== id);
    } else {
      newSelected = [...selectedOSIds, id];
    }
    setSelectedOSIds(newSelected);

    const newTotal = fetchedOS
      .filter(o => newSelected.includes(o.id))
      .reduce((sum, item) => sum + (Number(item.valor_total_repasses) || 0), 0);
    setValorMedicao(newTotal);
  };

  const toggleSelectAll = () => {
    if (selectedOSIds.length === fetchedOS.length) {
      setSelectedOSIds([]);
      setValorMedicao(0);
    } else {
      const allIds = fetchedOS.map(o => o.id);
      setSelectedOSIds(allIds);
      const total = fetchedOS.reduce((sum, item) => sum + (Number(item.valor_total_repasses) || 0), 0);
      setValorMedicao(total);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload: Partial<MedicoesAgregado> = {
        agregado_id: Number(agregadoId),
        periodo_inicial: periodoInicial,
        periodo_final: periodoFinal,
        valor_medicao: valorMedicao,
        status_medicao_id: statusMedicaoId
      };

      let medicaoId: number;
      if (selectedMedicao) {
        const { error } = await supabase.from('medicoes_agregado').update(payload).eq('id', selectedMedicao.id);
        if (error) throw error;
        medicaoId = selectedMedicao.id;
      } else {
        const { data, error } = await supabase.from('medicoes_agregado').insert([payload]).select().single();
        if (error) throw error;
        medicaoId = data.id;
      }

      // Link selected Service Orders to this medicao agregado
      // Em medições de agregados NÃO deve mudar o status da OS (apenas ao gerar medição de clientes)
      if (selectedOSIds.length > 0) {
        const { error: linkErr } = await supabase
          .from('ordem_de_servico')
          .update({ medicoes_agregado: medicaoId })
          .in('id', selectedOSIds);

        if (linkErr) {
          console.error('Falha ao associar OSs à medição de agregado:', linkErr);
          if (!selectedMedicao) {
            await supabase.from('medicoes_agregado').delete().eq('id', medicaoId);
          }
          throw new Error('Falha ao vincular as Ordens de Serviço: ' + linkErr.message);
        }
      }

      // Unlink any deselected OSs that belonged to this medicao (preserva status da OS)
      const unselectedIds = fetchedOS.filter(o => !selectedOSIds.includes(o.id)).map(o => o.id);
      if (unselectedIds.length > 0) {
        await supabase
          .from('ordem_de_servico')
          .update({ medicoes_agregado: null })
          .in('id', unselectedIds)
          .eq('medicoes_agregado', medicaoId);
      }

      alert('Medição de Agregado salva com sucesso!');
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error saving medicao agregado:', error);
      alert('Erro ao salvar medição: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-4xl rounded-xl bg-white shadow-2xl max-h-[95vh] flex flex-col">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4 flex-shrink-0 bg-slate-50 rounded-t-xl">
          <div>
            <h2 className="text-xl font-bold text-slate-800">
              {selectedMedicao ? 'Editar Medição / Agregado' : 'Gerar Medição / Agregado'}
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Puxe as ordens de serviço finalizadas ou aguardando assinatura do agregado no período para compor a medição.
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1 rounded-lg hover:bg-slate-200 transition-colors">
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSave} className="p-6 space-y-5 overflow-y-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-1">
              <label className="block text-sm font-semibold text-slate-700">Nº Medição</label>
              <input
                type="text"
                disabled
                className="mt-1 block w-full rounded-lg border border-slate-300 bg-slate-100 p-2.5 text-slate-600 font-bold"
                value={selectedMedicao?.id || 'Automático'}
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-semibold text-slate-700">
                Agregado * {selectedMedicao && <span className="text-xs text-slate-400 font-normal">(não editável)</span>}
              </label>
              <select
                required
                disabled={Boolean(selectedMedicao)}
                className={`mt-1 block w-full rounded-lg border p-2.5 shadow-sm font-medium transition-colors ${
                  selectedMedicao
                    ? 'border-slate-200 bg-slate-100 text-slate-600 cursor-not-allowed select-none'
                    : 'border-slate-300 bg-white text-slate-800 focus:border-red-500 focus:ring-red-500'
                }`}
                value={agregadoId}
                onChange={(e) => {
                  setAgregadoId(e.target.value ? Number(e.target.value) : '');
                  setHasCalculated(false);
                }}
              >
                <option value="">Selecione o Agregado...</option>
                {agregadoId && !agregados.some(a => a.id === agregadoId) && (
                  <option value={agregadoId}>
                    {selectedMedicao?.agregado_nome || `Agregado #${agregadoId}`}
                  </option>
                )}
                {agregados.map(a => (
                  <option key={a.id} value={a.id}>{a.nome}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-semibold text-slate-700">Período Inicial *</label>
              <input
                type="date"
                required
                className="mt-1 block w-full rounded-lg border border-slate-300 p-2.5 shadow-sm focus:border-red-500 focus:ring-red-500"
                value={periodoInicial}
                onChange={(e) => {
                  setPeriodoInicial(e.target.value);
                  setHasCalculated(false);
                }}
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700">Período Final *</label>
              <input
                type="date"
                required
                className="mt-1 block w-full rounded-lg border border-slate-300 p-2.5 shadow-sm focus:border-red-500 focus:ring-red-500"
                value={periodoFinal}
                onChange={(e) => {
                  setPeriodoFinal(e.target.value);
                  setHasCalculated(false);
                }}
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700">Status da Medição *</label>
              <select
                required
                className="mt-1 block w-full rounded-lg border border-slate-300 p-2.5 shadow-sm focus:border-red-500 focus:ring-red-500 font-medium"
                value={statusMedicaoId}
                onChange={(e) => setStatusMedicaoId(Number(e.target.value))}
              >
                {statusList.length > 0 ? (
                  statusList.map(st => (
                    <option key={st.id} value={st.id}>{st.nome}</option>
                  ))
                ) : (
                  <>
                    <option value={1}>Em Análise</option>
                    <option value={2}>Aprovado</option>
                    <option value={3}>Reprovado</option>
                    <option value={4}>Faturado</option>
                  </>
                )}
              </select>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 p-3 bg-slate-50 border border-slate-200 rounded-xl">
            <div className="text-xs text-slate-600 flex items-center gap-1.5">
              <Calculator className="w-4 h-4 text-emerald-600" />
              <span>Clique no botão ao lado para buscar e puxar todas as O.S. do agregado do período.</span>
            </div>
            <button
              type="button"
              onClick={handleGerarMedicao}
              disabled={calculating || !agregadoId || !periodoInicial || !periodoFinal}
              className="flex items-center gap-2 rounded-lg bg-emerald-600 px-5 py-2.5 text-white hover:bg-emerald-700 transition-all font-semibold shadow-sm disabled:opacity-50 cursor-pointer"
            >
              <Calculator className="h-4 w-4" />
              {calculating ? 'Puxando Ordens de Serviço...' : 'Gerar Medição (Puxar O.S.)'}
            </button>
          </div>

          {/* Table of Fetched Unlinked OS */}
          {hasCalculated && (
            <div className="border border-slate-200 rounded-xl overflow-hidden shadow-sm">
              <div className="bg-slate-100 px-4 py-3 border-b border-slate-200 flex flex-wrap justify-between items-center gap-2">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={toggleSelectAll}
                    className="text-xs font-bold text-slate-700 hover:text-slate-900 flex items-center gap-1 bg-white px-2.5 py-1 rounded border border-slate-300"
                  >
                    {selectedOSIds.length === fetchedOS.length && fetchedOS.length > 0 ? (
                      <CheckSquare className="w-3.5 h-3.5 text-emerald-600" />
                    ) : (
                      <Square className="w-3.5 h-3.5 text-slate-400" />
                    )}
                    Selecionar Todas ({selectedOSIds.length}/{fetchedOS.length})
                  </button>
                </div>

                <div className="flex items-center gap-3">
                  <div className="text-sm font-bold text-slate-800">
                    Subtotal Repasses: <span className="text-emerald-700">{new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valorMedicao)}</span>
                  </div>

                  {fetchedOS.length > 0 && (
                    <div className="flex items-center gap-1.5 border-l border-slate-300 pl-3">
                      <button
                        type="button"
                        onClick={() => {
                          const activeList = fetchedOS.filter(o => selectedOSIds.includes(o.id));
                          if (activeList.length === 0) {
                            alert('Selecione ao menos uma Ordem de Serviço para exportar.');
                            return;
                          }
                          const agrName = agregados.find(a => a.id === Number(agregadoId))?.nome;
                          try {
                            exportMedicaoToExcel(
                              {
                                id: selectedMedicao?.id || 'Previa',
                                type: 'agregado',
                                agregadoNome: agrName,
                                periodoInicial,
                                periodoFinal,
                                valorTotal: valorMedicao,
                                statusNome: statusList.find(s => s.id === statusMedicaoId)?.nome,
                              },
                              activeList.map(os => {
                                const qtdAjud = Number(os.quantidade_ajudante ?? os.qtd_ajudante ?? 0);
                                const valAjud = Number(os.ajudante_repasse || 0);
                                const totalRepasses = Number(os.valor_total_repasses || os.repasse || 0);
                                const tipoVeiculo = resolveTipoVeiculoDisplay(os);
                                const valUnitVeic = Number(
                                  os.valor_unit_servi ??
                                  (qtdAjud > 0 ? (totalRepasses - valAjud) : totalRepasses) ??
                                  0
                                );

                                return {
                                  id: os.id,
                                  data_atendimento: os.data_atendimento,
                                  cliente_nome: os.pedido?.pessoa?.nome || os.cliente_nome || '-',
                                  agregado_nome: agrName,
                                  motorista_nome: os.veiculos?.motorista?.nome || os.motorista?.nome || '-',
                                  placa: os.veiculos?.placa || os.placa || '-',
                                  tipo_veiculo: tipoVeiculo,
                                  descricao: os.descricao ? fixEncodingIssues(os.descricao) : '',
                                  valor_unit_servi: valUnitVeic,
                                  retirada: os.retirada || '',
                                  entrega: os.entrega || '',
                                  horario_inicio: os.horario_inicio || '',
                                  horario_final: os.horario_final || '',
                                  hora_total: os.hora_total || '',
                                  quantidade_ajudante: qtdAjud,
                                  valor_ajudante: valAjud,
                                  valor_total_repasses: totalRepasses,
                                  total_os: Number(os.total_os || 0),
                                };
                              })
                            );
                          } catch (err: any) {
                            console.error('Erro ao exportar medição:', err);
                            alert('Erro ao exportar medição para Excel: ' + (err?.message || 'Tente novamente.'));
                          }
                        }}
                        className="inline-flex items-center gap-1 px-2.5 py-1 bg-white hover:bg-emerald-50 text-emerald-700 border border-emerald-300 rounded text-xs font-semibold cursor-pointer shadow-2xs"
                        title="Exportar prévia em Excel"
                      >
                        <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-600" />
                        <span>Excel</span>
                      </button>

                      <button
                        type="button"
                        onClick={async () => {
                          const activeList = fetchedOS.filter(o => selectedOSIds.includes(o.id));
                          const agrName = agregados.find(a => a.id === Number(agregadoId))?.nome;
                          await exportMedicaoToPdf(
                            {
                              id: selectedMedicao?.id || 'Previa',
                              type: 'agregado',
                              agregadoNome: agrName,
                              periodoInicial,
                              periodoFinal,
                              valorTotal: valorMedicao,
                              statusNome: statusList.find(s => s.id === statusMedicaoId)?.nome,
                            },
                            activeList.map(os => ({
                              id: os.id,
                              data_atendimento: os.data_atendimento,
                              cliente_nome: os.pedido?.pessoa?.nome || os.cliente_nome || '-',
                              agregado_nome: agrName,
                              motorista_nome: os.veiculos?.motorista?.nome || os.motorista?.nome || '-',
                              placa: os.veiculos?.placa || os.placa || '-',
                              valor_total_repasses: Number(os.valor_total_repasses) || 0,
                            }))
                          );
                        }}
                        className="inline-flex items-center gap-1 px-2.5 py-1 bg-white hover:bg-red-50 text-red-700 border border-red-300 rounded text-xs font-semibold cursor-pointer shadow-2xs"
                        title="Exportar prévia em PDF"
                      >
                        <FileText className="w-3.5 h-3.5 text-red-600" />
                        <span>PDF</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="overflow-x-auto max-h-[260px] overflow-y-auto">
                <table className="min-w-full divide-y divide-slate-200">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">Sel.</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">Nº O.S</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">Status</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">Data</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">Placa / Veículo</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">Cliente</th>
                      <th className="px-3 py-2 text-right text-xs font-semibold text-slate-600">Repasse (R$)</th>
                      <th className="px-3 py-2 text-center text-xs font-semibold text-slate-600">Ver</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-slate-200 text-sm">
                    {fetchedOS.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="px-4 py-6 text-center text-slate-500">
                          <div className="flex flex-col items-center gap-2">
                            <AlertCircle className="w-6 h-6 text-amber-500" />
                            <span>Nenhuma Ordem de Serviço finalizada ou aguardando assinatura encontrada para este agregado no período.</span>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      fetchedOS.map(os => {
                        const isChecked = selectedOSIds.includes(os.id);
                        const sId = Number(os.status_id);
                        const statusBadge = sId === 4 
                          ? <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200">Finalizada</span>
                          : sId === 6 
                          ? <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-100 text-amber-800 border border-amber-200">Aguard. Assinatura</span>
                          : sId === 7
                          ? <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-blue-100 text-blue-800 border border-blue-200">Medição Gerada</span>
                          : <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-slate-100 text-slate-700">Status #{sId}</span>;

                        return (
                          <tr key={os.id} className={isChecked ? 'bg-emerald-50/40 hover:bg-emerald-50' : 'hover:bg-slate-50 opacity-70'}>
                            <td className="px-3 py-2">
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => toggleSelectOS(os.id)}
                                className="w-4 h-4 text-emerald-600 rounded border-slate-300 focus:ring-emerald-500 cursor-pointer"
                              />
                            </td>
                            <td className="px-3 py-2 font-bold text-slate-900">{os.id}</td>
                            <td className="px-3 py-2 whitespace-nowrap">{statusBadge}</td>
                            <td className="px-3 py-2 text-slate-600">
                              {formatDateBR(os.data_atendimento)}
                            </td>
                            <td className="px-3 py-2 font-medium text-slate-800">
                              {os.veiculos?.placa || os.placa || '-'}
                            </td>
                            <td className="px-3 py-2 text-slate-600">
                              {os.pedido?.pessoa?.nome || os.cliente_nome || '-'}
                            </td>
                            <td className="px-3 py-2 text-right font-bold text-slate-900">
                              {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(os.valor_total_repasses) || 0)}
                            </td>
                            <td className="px-3 py-2 text-center">
                              <button
                                type="button"
                                onClick={() => setViewingOsId(os.id)}
                                className="text-slate-600 hover:text-slate-900 p-1 rounded hover:bg-slate-200"
                                title="Visualizar O.S"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-semibold text-slate-700">Valor Total da Medição / Repasse (R$)</label>
            <input
              type="number"
              step="0.01"
              required
              className="mt-1 block w-full rounded-lg border border-slate-300 p-2.5 shadow-sm focus:border-red-500 focus:ring-red-500 font-bold text-slate-900 text-lg bg-emerald-50/50"
              value={valorMedicao}
              onChange={(e) => setValorMedicao(Number(e.target.value))}
            />
          </div>

          {selectedMedicao && (
            <MedicaoOSList 
              medicaoId={selectedMedicao.id} 
              type="agregado" 
              onUpdate={onSuccess}
              agregadoNome={agregados.find(a => a.id === Number(agregadoId))?.nome || selectedMedicao.agregado_nome || (selectedMedicao as any).pessoa?.nome}
              periodoInicial={periodoInicial}
              periodoFinal={periodoFinal}
              valorTotal={valorMedicao}
              statusNome={statusList.find(s => s.id === statusMedicaoId)?.nome}
            />
          )}

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-slate-300 px-6 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center gap-2 rounded-lg bg-red-600 px-6 py-2.5 text-sm font-semibold text-white hover:bg-red-700 shadow-md transition-all disabled:opacity-50"
            >
              <Save className="h-4 w-4" />
              {loading ? 'Salvando...' : 'Salvar Registro'}
            </button>
          </div>
        </form>
      </div>

      {viewingOsId && (
        <VisualizarOSModal
          osId={viewingOsId}
          onClose={() => setViewingOsId(null)}
        />
      )}
    </div>
  );
}

